import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCustomerInvoiceLinks } from '../../services/orders';
import { getCreditBalance } from '../../services/auth';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { PAYMENT_STATUS_LABELS } from '../../utils/constants';
import { FULFILLMENT_LABELS } from '../../utils/orderConstants';
import useOrders from '../../hooks/useOrders';
import Loading from '../../components/common/Loading';
import Alert from '../../components/common/Alert';
import './Invoices.css';

const FILTERS = ['all', 'unpaid', 'paid'];

const InvoiceRow = ({ order }) => {
  const [links, setLinks] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [linkError, setLinkError] = useState(false);
  const navigate = useNavigate();

  const handleGetLinks = async () => {
    setFetching(true);
    setLinkError(false);
    try {
      const data = await getCustomerInvoiceLinks(order.id);
      setLinks(data);
    } catch {
      setLinkError(true);
    } finally {
      setFetching(false);
    }
  };

  const status = order.payment_status || 'open';
  const sessionLabel = order.session_detail?.label;
  const fulfillment = FULFILLMENT_LABELS[order.fulfillment_type] || null;

  return (
    <div className={`invoices-page__row invoices-page__row--${status}`}>

      {/* Left: order info */}
      <div className="invoices-page__row-left">
        <div className="invoices-page__row-top">
          <button
            className="invoices-page__order-link"
            onClick={() => navigate(`/orders/${order.id}`)}
          >
            {order.order_number}
          </button>
          <span className={`invoices-page__badge invoices-page__badge--${status}`}>
            {PAYMENT_STATUS_LABELS[status] || status}
          </span>
        </div>
        <div className="invoices-page__row-sub">
          {sessionLabel && <span>{sessionLabel}</span>}
          {sessionLabel && fulfillment && <span className="invoices-page__dot">·</span>}
          {fulfillment && <span>{fulfillment}</span>}
        </div>
        {order.po_number && (
          <div className="invoices-page__row-po">
            PO: {order.po_number}
            {order.po_notes && ` — ${order.po_notes}`}
          </div>
        )}
      </div>

      {/* Right: amount + dates + links */}
      <div className="invoices-page__row-right">
        <div className="invoices-page__row-amount">
          {formatCurrency(order.total_amount_cents / 100)}
        </div>
        <div className="invoices-page__row-dates">
          {order.payment_date && (
            <span className="invoices-page__date-label">
              Paid {formatDate(order.payment_date)}
            </span>
          )}
          {!order.payment_date && order.due_date && (
            <span className={`invoices-page__date-label${(status === 'overdue' || status === 'payment_failed') ? ' invoices-page__date-label--overdue' : ''}`}>
              Due {formatDate(order.due_date)}
            </span>
          )}
          {!order.payment_date && !order.due_date && order.invoice_sent_at && (
            <span className="invoices-page__date-label">
              Sent {formatDate(order.invoice_sent_at)}
            </span>
          )}
        </div>
        <div className="invoices-page__row-actions">
          {links ? (
            <>
              {links.hosted_invoice_url && (
                <a
                  href={links.hosted_invoice_url}
                  target="_blank"
                  rel="noreferrer"
                  className="invoices-page__action-btn"
                >
                  View Invoice ↗
                </a>
              )}
              {links.invoice_pdf && (
                <a
                  href={links.invoice_pdf}
                  target="_blank"
                  rel="noreferrer"
                  className="invoices-page__action-btn invoices-page__action-btn--secondary"
                >
                  PDF ↗
                </a>
              )}
            </>
          ) : (
            <>
              <button
                className="invoices-page__action-btn"
                disabled={fetching}
                onClick={handleGetLinks}
              >
                {fetching ? 'Loading…' : 'Get Links'}
              </button>
              {linkError && (
                <span className="invoices-page__link-error">Failed — try again</span>
              )}
            </>
          )}
        </div>
      </div>

    </div>
  );
};

const Invoices = () => {
  const { orders, loading, error } = useOrders();
  const [filter, setFilter] = useState('all');
  const [creditBalance, setCreditBalance] = useState(null);

  useEffect(() => {
    getCreditBalance()
      .then((data) => setCreditBalance(data.credit_balance_cents))
      .catch(() => {});
  }, []);

  const invoiced = orders.filter((o) => o.stripe_invoice_id);

  const filtered = filter === 'all'
    ? invoiced
    : filter === 'unpaid'
      ? invoiced.filter((o) => o.payment_status === 'open' || o.payment_status === 'overdue' || o.payment_status === 'payment_failed')
      : invoiced.filter((o) => o.payment_status === filter);

  return (
    <div className="invoices-page">
      <div className="invoices-page__header">
        <div>
          <p className="invoices-page__eyebrow">Deep Roots Milling</p>
          <h1 className="invoices-page__title">Invoices</h1>
        </div>
      </div>

      <hr className="invoices-page__rule" />

      {loading && (
        <div className="invoices-page__loading">
          <Loading />
        </div>
      )}

      {error && <Alert type="error">{error.message || 'Failed to load invoices.'}</Alert>}

      {creditBalance > 0 && (
        <div className="invoices-page__credit-banner">
          You have <strong>{formatCurrency(creditBalance / 100)}</strong> in account credit — it will be automatically applied to your next invoice.
        </div>
      )}

      {!loading && !error && (
        <>
          <div className="invoices-page__filters">
            {FILTERS.map((f) => (
              <button
                key={f}
                className={`invoices-page__filter-btn${filter === f ? ' invoices-page__filter-btn--active' : ''}`}
                onClick={() => setFilter(f)}
              >
                {f === 'all' ? 'All' : f === 'unpaid' ? 'Unpaid' : PAYMENT_STATUS_LABELS[f]}
              </button>
            ))}
          </div>

          {filtered.length === 0 ? (
            <p className="invoices-page__empty">
              {filter === 'all'
                ? 'No invoices yet.'
                : filter === 'unpaid'
                  ? 'No unpaid invoices.'
                  : `No ${PAYMENT_STATUS_LABELS[filter].toLowerCase()} invoices.`}
            </p>
          ) : (
            <div className="invoices-page__list">
              {filtered.map((order) => (
                <InvoiceRow key={order.id} order={order} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Invoices;
